/**
 * Public "pay your invoice" page. Loaded when the customer clicks the
 * one-click pay link in their invoice email:
 *
 *     /api/pay/{invoice_id}?t=<token>  →  303  →  /pay/{invoice_id}?t=<token>
 *
 * Step 1: Show invoice total + two payment method buttons (ACH 1%, Card 3%).
 * Step 2: Embed Stripe Checkout on our domain via <EmbeddedCheckoutProvider>.
 *
 * Convenience-fee copy must match the fee logic in
 * ``backend/public_routes.py`` (_FEE_RATES).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
    EmbeddedCheckoutProvider, EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import {
    Landmark, CreditCard, ShieldCheck, Loader2, ArrowLeft, XCircle,
} from "lucide-react";
import { company } from "@/lib/branding";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
// Memoize outside the component to avoid re-creating the Stripe instance
// on every render (breaks the embedded provider).
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function InvoicePay() {
    const { invoiceId } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("t") || "";

    const [state, setState] = useState({ status: "loading" });
    const [method, setMethod] = useState(null); // "ach" | "card"
    const [clientSecret, setClientSecret] = useState(null);
    const [sessionErr, setSessionErr] = useState(null);
    const [creating, setCreating] = useState(false);

    const base = process.env.REACT_APP_BACKEND_URL || "";

    // Load invoice details once
    useEffect(() => {
        if (!invoiceId || !token) {
            setState({ status: "error", message: "Missing invoice or token." });
            return;
        }
        fetch(`${base}/api/pay/${invoiceId}/details?t=${encodeURIComponent(token)}`)
            .then(async (r) => {
                if (!r.ok) {
                    const body = await r.json().catch(() => ({}));
                    throw new Error(body.detail || `Error ${r.status}`);
                }
                return r.json();
            })
            .then((data) => setState({ status: "ok", data }))
            .catch((e) => setState({ status: "error", message: e.message }));
    }, [invoiceId, token, base]);

    const createSession = async (chosen) => {
        setCreating(true);
        setSessionErr(null);
        try {
            const res = await fetch(`${base}/api/pay/${invoiceId}/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ method: chosen, t: token }),
            });
            const body = await res.json();
            if (!res.ok) throw new Error(body.detail || `Error ${res.status}`);
            setMethod(chosen);
            setClientSecret(body.client_secret);
        } catch (e) {
            setSessionErr(e.message);
        } finally {
            setCreating(false);
        }
    };

    const inv = state.data;
    const fees = inv?.fees;

    // Once the customer has picked, and we have a client secret, render
    // Stripe Embedded Checkout on our domain.
    const embedOptions = useMemo(
        () => (clientSecret ? { clientSecret } : null),
        [clientSecret],
    );

    if (state.status === "loading") {
        return (
            <Shell>
                <div className="p-10 text-center">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                    <p className="mt-3 text-sm text-muted-foreground">Loading invoice…</p>
                </div>
            </Shell>
        );
    }

    if (state.status === "error") {
        return (
            <Shell>
                <div className="p-10 text-center">
                    <XCircle className="w-12 h-12 mx-auto text-red-500" />
                    <h2 className="mt-4 font-heading text-xl font-bold">Can't open this invoice</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        {state.message || "Please reply to your invoice email if you need help."}
                    </p>
                </div>
            </Shell>
        );
    }

    // Step 2: embedded checkout
    if (clientSecret && stripePromise && embedOptions) {
        return (
            <Shell>
                <div className="px-4 pt-4 pb-2 border-b flex items-center justify-between">
                    <button
                        type="button"
                        data-testid="pay-back-btn"
                        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                        onClick={() => { setClientSecret(null); setMethod(null); }}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Change payment method
                    </button>
                    <div className="text-xs text-muted-foreground">
                        {method === "ach" ? "Bank Account · 1% fee" : "Card · 3% fee"}
                    </div>
                </div>
                <div id="stripe-embedded-wrap" data-testid="stripe-embedded-checkout">
                    <EmbeddedCheckoutProvider
                        stripe={stripePromise}
                        options={embedOptions}
                    >
                        <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                </div>
            </Shell>
        );
    }

    // Step 1: picker
    return (
        <Shell>
            <div className="p-6 border-b bg-secondary/40">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Invoice {inv.invoice_number || invoiceId.slice(0, 8)}
                </div>
                <div className="mt-1 font-heading text-lg font-semibold">
                    {inv.customer?.name || `${company()} customer`}
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-xs text-muted-foreground">Amount due</span>
                    <span
                        data-testid="pay-base-amount"
                        className="font-heading text-3xl font-bold"
                    >
                        {money(inv.base_amount)}
                    </span>
                </div>
            </div>

            <div className="p-6 space-y-4">
                <div className="text-sm font-medium">Choose how you'd like to pay</div>

                <MethodCard
                    testid="pay-method-ach"
                    icon={<Landmark className="w-6 h-6 text-emerald-600" />}
                    title="Bank Account (ACH)"
                    subtitle="Lowest fee — pay directly from your checking account"
                    feeLabel={`+ ${money(fees.ach.amount)} convenience fee (1%)`}
                    total={fees.ach.total}
                    onClick={() => createSession("ach")}
                    disabled={creating}
                    loading={creating && method === "ach"}
                />

                <MethodCard
                    testid="pay-method-card"
                    icon={<CreditCard className="w-6 h-6 text-blue-600" />}
                    title="Card / Apple Pay"
                    subtitle="Visa, Mastercard, Discover, Amex, Apple Pay"
                    feeLabel={`+ ${money(fees.card.amount)} convenience fee (3%)`}
                    total={fees.card.total}
                    onClick={() => createSession("card")}
                    disabled={creating}
                    loading={creating && method === "card"}
                />

                {sessionErr && (
                    <div
                        data-testid="pay-session-error"
                        className="text-sm text-red-600 border border-red-200 bg-red-50 rounded-lg p-3"
                    >
                        {sessionErr}
                    </div>
                )}

                <div className="pt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <ShieldCheck className="w-4 h-4" />
                    Secure checkout powered by Stripe · Your payment is encrypted end to end
                </div>
            </div>
        </Shell>
    );
}

function MethodCard({
    testid, icon, title, subtitle, feeLabel, total, onClick, disabled, loading,
}) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            disabled={disabled}
            className={
                "w-full text-left p-4 rounded-xl border bg-background " +
                "hover:border-primary hover:shadow-sm transition-all " +
                "disabled:opacity-60 disabled:cursor-not-allowed " +
                "flex items-center gap-4"
            }
        >
            <div className="shrink-0 w-11 h-11 rounded-full bg-secondary flex items-center justify-center">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : icon}
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-medium">{title}</div>
                <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
                <div className="text-xs text-muted-foreground mt-1">{feeLabel}</div>
            </div>
            <div className="text-right">
                <div className="text-xs text-muted-foreground">Total</div>
                <div className="font-heading text-lg font-bold">{money(total)}</div>
            </div>
        </button>
    );
}

function Shell({ children }) {
    return (
        <div
            data-testid="invoice-pay-page"
            className="min-h-screen bg-secondary/40 flex items-start sm:items-center justify-center p-0 sm:p-4"
        >
            <div className="bg-background w-full sm:max-w-lg sm:rounded-2xl shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <div className="font-heading text-lg font-bold">{company()}</div>
                    <div className="text-xs text-muted-foreground">Pay invoice</div>
                </div>
                {children}
            </div>
        </div>
    );
}
